import type { RouterContextType } from "@/api/permissions/permissions.api";
import { SERVICE_PROVIDER } from "@/components/data/constants";
import { getActiveSessionState } from "@/hooks/useActiveSession";
import { useSpInviteSessionStore } from "@/store/useSpInviteSessionStore";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { Anchor, Box, Image, List, Stack, Text } from "@mantine/core";
import { Button, Card } from "@onengine/component-library";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import styles from "./SpInvite.module.scss";

export const Route = createFileRoute("/sp-invite/")({
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
    const { userType } = getActiveSessionState();
    const splashData = useSpInviteSessionStore.getState().splashData;
    const status = splashData?.onboardingStatus?.value;
    const isSplashEligible = status === "invited";
    if (userType !== SERVICE_PROVIDER || !splashData?.shouldShowSplash || !isSplashEligible) {
      throw redirect({ to: "/" });
    }
  },
  component: SpInvite,
});

function SpInvite() {
  const navigate = useNavigate();
  const providerType = useSpInviteSessionStore(
    (state) => state.splashData?.serviceProviderType?.value,
  );

  const handleBeginReview = () => {
    if (!providerType) return;
    navigate({
      to: "/sp-invite/review/$providerType/$stepId",
      params: { providerType, stepId: "personal-info" },
    });
  };

  const WORK_ORDER_UPLOAD_REQUIREMENTS = [
    {
      title: "Before we can dispatch work orders, you’ll need to upload the following:",

      items: [
        {
          id: "drivers-license",
          description: <Text>State issued driver’s license (front and back)</Text>,
        },
        {
          id: "waist-up-image",
          description: <Text>A waist-up image of yourself</Text>,
        },
        {
          id: "headshot-image",
          description: <Text>A headshot image of yourself</Text>,
        },
        {
          id: "vehicle-image",
          description: <Text>An image of your work vehicle, with license plate visible</Text>,
        },
        {
          id: "form-w9",
          description: (
            <Text>
              A completed and signed{" "}
              <Anchor
                href="https://www.irs.gov/pub/irs-pdf/fw9.pdf"
                target="_blank"
                underline="always"
                style={{ textUnderlineOffset: "var(--spacing-4x-small, 2px)" }}
              >
                Form W9
              </Anchor>
            </Text>
          ),
        },
      ],
    },
    {
      title: "The following documents may be necessary depending on your case:",
      items: [
        {
          id: "license-certificate",
          description: <Text>Any required trade licenses or certifications</Text>,
        },
        {
          id: "coi",
          description: <Text>Certificates of Insurance (COI)</Text>,
        },
      ],
    },
  ];

  return (
    <Box className={styles.spInviteContainer}>
      <Stack style={{ maxWidth: 600 }} gap={"var(--spacing-x-large, 24px)"}>
        <Stack gap={"var(--spacing-x-large, 24px)"}>
          <Image style={{ width: "50px" }} src={"/favicon.svg"} alt="Success" />
          <Text className={styles.welcomeHeaderText}>Welcome to ServEngine!</Text>
        </Stack>
        <Card style={{ padding: "var(--spacing-medium, 16px)" }}>
          <Stack gap={"var(--spacing-x-large, 24px)"}>
            <Text className={styles.welcomeSubHeaderText}>Complete Your Onboarding</Text>
            <Text className={styles.welcomeDescriptionText}>
              The following pages contain all the information you provided during your onboarding
              call. Please review the details entered on your behalf and make any necessary
              corrections.
            </Text>
            <Text className={styles.welcomeDescriptionText}>
              {" "}
              This process should take around 10 minutes.
            </Text>
          </Stack>
        </Card>
        <Card style={{ padding: "var(--spacing-medium, 16px)" }}>
          <Stack gap={"var(--spacing-x-large, 24px)"}>
            <Text className={styles.welcomeSubHeaderText}>Additional Documents Needed</Text>
            <Stack gap={"var(--spacing-medium, 16px)"}>
              {WORK_ORDER_UPLOAD_REQUIREMENTS.map((requirement) => (
                <Stack key={requirement.title} gap={"var(--spacing-4x-small, 2px)"}>
                  <Text className={styles.requirementsTitle} fw={500}>
                    {requirement.title}
                  </Text>
                  <List
                    spacing={"var(--spacing-4x-small, 2px)"}
                    className={styles.requirementsList}
                  >
                    {requirement.items.map((item) => (
                      <List.Item
                        key={item.id}
                        style={{ lineHeight: "var(--text-line-height-large, 24px)" }}
                        className={styles.welcomeDescriptionText}
                      >
                        {item.description}
                      </List.Item>
                    ))}
                  </List>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Card>
        <Button variant="primary" style={{ marginLeft: "auto" }} onClick={handleBeginReview}>
          Begin Review
        </Button>
      </Stack>
    </Box>
  );
}
